import React, { useState } from "react";
import {
  GetFloorStatus,
  getFloorToken,
  getFloorUrl,
  isFloorAuthError,
  setFloorToken,
  setFloorUrl,
} from "../../api/floorApi";
import {
  FloorStatusLine,
  SecondaryButton,
  SettingsBox,
} from "./LavaFloorPageStyled";

// Floor connection settings (address + token) with a "Test" button.
// Shown on the Home screen (tap the LAVA FLOOR AUTO label) so the referee
// can configure and verify the floor BEFORE the event, and on the Lava
// Floor page (gear icon). Values persist per device in localStorage.

type TestState =
  | { kind: "idle" }
  | { kind: "testing" }
  | { kind: "ok"; version: string; sim: boolean }
  | { kind: "auth" }
  | { kind: "offline" };

type Props = {
  ro: boolean;
  onSaved?: () => void;
};

export const FloorSettings = ({ ro, onSaved }: Props) => {
  const [urlDraft, setUrlDraft] = useState(getFloorUrl());
  const [tokenDraft, setTokenDraft] = useState(getFloorToken());
  const [test, setTest] = useState<TestState>({ kind: "idle" });

  const persist = () => {
    setFloorUrl(urlDraft);
    setFloorToken(tokenDraft);
    setUrlDraft(getFloorUrl());
    setTokenDraft(getFloorToken());
  };

  const runTest = async () => {
    persist();
    setTest({ kind: "testing" });
    try {
      const s = await GetFloorStatus();
      setTest({ kind: "ok", version: s.version, sim: !!s.sim });
    } catch (e) {
      setTest({ kind: isFloorAuthError(e) ? "auth" : "offline" });
    }
  };

  const save = () => {
    persist();
    if (onSaved) onSaved();
  };

  const testLine = (() => {
    switch (test.kind) {
      case "testing":
        return { online: true, text: ro ? "Se testează…" : "Testing…" };
      case "ok":
        return {
          online: true,
          text: `${ro ? "Podea conectată" : "Floor connected"} (${
            test.version
          })${test.sim ? " — SIM" : ""}`,
        };
      case "auth":
        return {
          online: false,
          text: ro ? "Token greșit" : "Wrong token",
        };
      case "offline":
        return {
          online: false,
          text: ro
            ? "Podea inaccesibilă — verifică adresa / check.bat pe PC"
            : "Floor unreachable — check the address / run check.bat on the PC",
        };
      default:
        return null;
    }
  })();

  return (
    <SettingsBox>
      <small>
        {ro
          ? "Adresa podelei (link Tailscale, sau http://ip:8000 în rețeaua locală)"
          : "Floor address (Tailscale link, or http://ip:8000 on the local network)"}
      </small>
      <input
        value={urlDraft}
        onChange={(e) => setUrlDraft(e.target.value)}
        placeholder="https://floor-pc.tailxxxx.ts.net"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        inputMode="url"
      />
      <small>
        {ro
          ? "Token (remote_token.txt de pe calculatorul podelei; gol pentru rețeaua locală)"
          : "Token (remote_token.txt on the floor machine; empty for LAN mode)"}
      </small>
      <input
        value={tokenDraft}
        onChange={(e) => setTokenDraft(e.target.value)}
        placeholder="k7mp3xw9q2rt5vhn"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
      />
      {testLine && (
        <FloorStatusLine $online={testLine.online}>
          ● {testLine.text}
        </FloorStatusLine>
      )}
      <SecondaryButton onClick={runTest} disabled={test.kind === "testing"}>
        <p>{ro ? "Testează conexiunea" : "Test connection"}</p>
      </SecondaryButton>
      <SecondaryButton onClick={save}>
        <p>{ro ? "Salvează" : "Save"}</p>
      </SecondaryButton>
    </SettingsBox>
  );
};
